import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import { hotNews, categories } from "@/mocks/hotNews";
import { getCategory, formatViews } from "@/utils/content";

export default function Ranking() {
  const [active, setActive] = useState("all");
  const [sort, setSort] = useState<"heat" | "views">("heat");

  const list = useMemo(() => {
    const src = active === "all" ? hotNews : hotNews.filter((n) => n.categoryId === active);
    return [...src].sort((a, b) => (sort === "heat" ? b.heat - a.heat : b.views - a.views));
  }, [active, sort]);

  const rankingLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "在线吃瓜今日热瓜实时榜单",
    numberOfItems: list.length,
    itemListElement: list.map((n, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: n.title,
      url: `https://www.zgchigua.cn/news/detail/${n.slug}`,
    })),
  };
  const crumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.zgchigua.cn/" },
      { "@type": "ListItem", position: 2, name: "今日热瓜实时榜单", item: "https://www.zgchigua.cn/ranking" },
    ],
  };

  return (
    <Layout>
      <Seo
        title="今日热瓜实时榜单_最新热度排行_在线吃瓜"
        description="在线吃瓜今日热瓜实时榜单，综合全网搜索量、讨论速度与转发趋势，实时呈现此刻最受关注的热门话题与新鲜事热度排行，助你快速锁定焦点动态。"
        keywords="今日热瓜,实时榜单,热门话题排行,最新进展,新鲜事热度,在线吃瓜"
        path="/ranking"
        jsonLd={[rankingLd, crumbLd]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-secondary-500"></div>
        <div className="container-x relative py-10 md:py-14 text-background-50">
          <nav className="flex items-center gap-1 text-xs text-background-100/80" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>今日热瓜实时榜单</span>
          </nav>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <span className="chip gap-2 bg-background-50/15 px-3 py-1 text-xs font-bold backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-300 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-400"></span>
              </span>
              实时更新中
            </span>
            <h1 className="font-heading text-3xl md:text-4xl">今日热瓜实时榜单</h1>
          </div>
          <p className="mt-3 max-w-2xl text-sm text-background-100/90">
            热度值综合搜索指数、原创与转发讨论量、讨论增速及权威信源提及频率加权得出，每 5 分钟刷新一次。想知道此刻大家正在聊什么，看这里就够了。
          </p>
        </div>
      </section>

      <section className="container-x py-8 md:py-10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="no-scrollbar flex gap-2 overflow-x-auto">
            <button onClick={() => setActive("all")} className={`chip px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${active === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100"}`}>
              全部热榜
            </button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setActive(c.id)} className={`chip px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${active === c.id ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100"}`}>
                <i className={c.icon}></i>{c.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="text-foreground-500">排序：</span>
            <button onClick={() => setSort("heat")} className={`chip px-3 py-1.5 cursor-pointer transition-colors ${sort === "heat" ? "bg-secondary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-secondary-100"}`}>
              <i className="ri-fire-line"></i>综合热度
            </button>
            <button onClick={() => setSort("views")} className={`chip px-3 py-1.5 cursor-pointer transition-colors ${sort === "views" ? "bg-secondary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-secondary-100"}`}>
              <i className="ri-eye-line"></i>阅读量
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-9">
            <ol className="card-surface divide-y divide-background-200/70 px-2 sm:px-4">
              {list.map((item, idx) => {
                const rank = idx + 1;
                const cat = getCategory(item.categoryId);
                const badgeClass =
                  rank === 1 ? "bg-accent-500 text-background-50" : rank === 2 ? "bg-background-300 text-foreground-800" : rank === 3 ? "bg-primary-200 text-primary-900" : "bg-background-200 text-foreground-500";
                return (
                  <li key={item.slug}>
                    <Link to={`/news/detail/${item.slug}`} className="group flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-background-100 md:gap-4 md:py-4">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-heading text-base ${badgeClass}`}>
                        {rank}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h2 className="line-clamp-2 text-sm font-semibold leading-snug group-hover:text-primary-700 transition-colors md:text-base">{item.title}</h2>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                          <span className="chip bg-background-100 px-2 py-0.5 text-primary-700"><i className={cat.icon}></i>{cat.name}</span>
                          <span>{item.tag}</span>
                          <span>{item.date} {item.time}</span>
                        </div>
                      </div>
                      <div className="hidden shrink-0 text-right sm:block">
                        <div className="flex items-center justify-end gap-1 text-base font-bold text-primary-600"><i className="ri-fire-line"></i>{item.heat}</div>
                        <div className="mt-1 flex items-center justify-end gap-1 text-xs text-foreground-500"><i className="ri-eye-line"></i>{formatViews(item.views)}</div>
                      </div>
                      <i className="ri-arrow-right-s-line shrink-0 text-xl text-foreground-300 group-hover:text-primary-500"></i>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </div>

          <aside className="lg:col-span-3 space-y-5">
            <div className="card-surface p-5">
              <h3 className="font-heading text-lg text-primary-700"><i className="ri-information-line"></i> 榜单规则</h3>
              <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                <li className="flex gap-2"><i className="ri-check-line text-primary-600"></i>每 5 分钟自动更新</li>
                <li className="flex gap-2"><i className="ri-check-line text-primary-600"></i>综合搜索与讨论趋势加权</li>
                <li className="flex gap-2"><i className="ri-check-line text-primary-600"></i>多维度信号交叉验证</li>
                <li className="flex gap-2"><i className="ri-check-line text-primary-600"></i>高热度内容自动上榜</li>
              </ul>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-secondary-100 to-primary-100 p-5">
              <h3 className="font-heading text-lg text-secondary-900">想跟某个瓜到底？</h3>
              <p className="mt-2 text-sm text-foreground-600">点进标题即可查看完整事件进展、相关背景与最新回应。</p>
              <Link to="/news" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary-700 hover:underline">
                浏览最新资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}